
import React, { useState, useRef, useCallback, useEffect } from "react";
import * as Material from "@mui/material";
import { Text, Table } from "@fragments";
import { IconCircleFilled, IconUsers, IconCalendar, IconAdjustments } from "@tabler/icons-react";

import api from "@helpers/api.js";
import Parser from "@helpers/parser.js";
import { useCommunity } from "contexts/community";

import Popper from "@fragments/Popper";
import { UserAvatar } from "@components/Community";
import StatusChip from "@fragments/StatusChip";

import SearchBar from "../../components/SearchBar";
import MetricCard from "../../components/MetricCard";
import Filter from "../../components/Filter";

import "./styles.scss";

export default function SubscriptionsSection() {
    const sharedPopperRef = useRef();
    const { communities } = useCommunity();

    // Estados compartilhados entre os componentes
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedCommunity, setSelectedCommunity] = useState('all');
    const [selectedDateFilter, setSelectedDateFilter] = useState('always');
    const [searchMode, setSearchMode] = useState('email');
    const searchQueryRef = useRef('');

    // Estados de dados e loading centralizados
    const [subscriptions, setSubscriptions] = useState([]);
    const [summary, setSummary] = useState({
        active: { records: 0, amount: 0 },
        canceled: { records: 0, amount: 0 },
        expired: { records: 0, amount: 0 }
    });
    const [loading, setLoading] = useState(true);

    // Estados de paginação
    const [pagination, setPagination] = useState({
        pages: 0,
        records: 0,
        page: 1
    });

    const rowsSize = 15;

    const fetchData = useCallback(async (searchValue = searchQueryRef.current, pageNumber = pagination.page) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();

            if (selectedStatus !== 'all') {
                params.append('filter[status]', selectedStatus);
            }

            if (selectedCommunity !== 'all') {
                params.append('community', selectedCommunity);
            }

            // Adicionar filtros de data
            if (selectedDateFilter !== 'always') {
                const today = new Date();
                let dateFrom = null;
                let dateTo = null;

                switch (selectedDateFilter) {
                    case 'today':
                        dateFrom = today.toISOString().split('T')[0];
                        dateTo = today.toISOString().split('T')[0];
                        break;
                    case 'last_week':
                        const lastWeek = new Date(today);
                        lastWeek.setDate(today.getDate() - 7);
                        dateFrom = lastWeek.toISOString().split('T')[0];
                        dateTo = today.toISOString().split('T')[0];
                        break;
                    case 'last_30_days':
                        const last30Days = new Date(today);
                        last30Days.setDate(today.getDate() - 30);
                        dateFrom = last30Days.toISOString().split('T')[0];
                        dateTo = today.toISOString().split('T')[0];
                        break;
                }

                if (dateFrom) {
                    params.append('filter[date_from]', dateFrom);
                }
                if (dateTo) {
                    params.append('filter[date_to]', dateTo);
                }
            }

            if (searchValue.trim()) {
                if (searchMode === 'email') {
                    params.append('filter[email]', searchValue.trim());
                } else {
                    params.append('filter[name]', searchValue.trim());
                }
            }

            // Adicionar parâmetros de paginação
            params.append('page', pageNumber.toString());
            params.append('per_page', rowsSize);

            const { data } = await api.get(`subscription/search?${params.toString()}`);

            setSubscriptions(data.data || []);
            setSummary(data.summary || {
                active: { records: 0, amount: 0 },
                canceled: { records: 0, amount: 0 },
                expired: { records: 0, amount: 0 }
            });

            // Atualizar dados de paginação
            setPagination({
                pages: data.pages || 0,
                records: data.records || 0,
                page: data.page || 1
            });

        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            setSubscriptions([]);
            setSummary({
                active: { records: 0, amount: 0 },
                canceled: { records: 0, amount: 0 },
                expired: { records: 0, amount: 0 }
            });
            setPagination({
                pages: 0,
                records: 0,
                page: 1
            });
        } finally {
            setLoading(false);
        }
    }, [selectedStatus, selectedCommunity, selectedDateFilter, searchMode, pagination.page]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchData(searchQueryRef.current, 1);
    }, [selectedStatus, selectedCommunity, selectedDateFilter, searchMode]);

    const executeSearch = useCallback(() => {
        fetchData(searchQueryRef.current);
    }, [fetchData]);

    const handlePageChange = useCallback((newPage) => {
        fetchData(searchQueryRef.current, newPage);
    }, [fetchData]);

    function FiltersRow() {
        const statusOptions = [
            { id: 'all', label: 'Todas' },
            { id: 'active', label: 'Ativa' },
            { id: 'canceled', label: 'Cancelada' },
            { id: 'expired', label: 'Expirada' },
        ];

        const communityOptions = [
            { id: 'all', label: 'Todas' },
            ...(communities || []).filter(comm => comm?.isAdmin()).map(comm => ({
                id: comm.id,
                label: comm.name
            }))
        ];

        const dateFilterOptions = [
            { id: 'always', label: 'Sempre' },
            { id: 'today', label: 'Hoje' },
            { id: 'last_week', label: 'Última semana' },
            { id: 'last_30_days', label: 'Últimos 30 dias' },
        ];

        function handleStatusFilterClick(event) {
            const options = statusOptions.map(option => ({
                ...option,
                onClick: () => handleStatusSelect(option.id)
            }));

            sharedPopperRef.current.open(options, 'bottom', event.currentTarget);
        }

        function handleCommunityFilterClick(event) {
            const options = communityOptions.map(option => ({
                ...option,
                onClick: () => handleCommunitySelect(option.id)
            }));

            sharedPopperRef.current.open(options, 'bottom', event.currentTarget);
        }

        function handleStatusSelect(status) {
            setSelectedStatus(status);
            sharedPopperRef.current.close();
        }

        function handleCommunitySelect(communityId) {
            setSelectedCommunity(communityId);
            sharedPopperRef.current.close();
        }

        function handleDateFilterClick(event) {
            const options = dateFilterOptions.map(option => ({
                ...option,
                onClick: () => handleDateFilterSelect(option.id)
            }));

            sharedPopperRef.current.open(options, 'bottom', event.currentTarget);
        }

        function handleDateFilterSelect(dateFilter) {
            setSelectedDateFilter(dateFilter);
            sharedPopperRef.current.close();
        }

        return (
            <>
                <Text.h4 value="Filtros" className="mt-3" left={<IconAdjustments />} />


                <div className="filters-row mt-1">

                    <Filter
                        name="Status"
                        valueLabel={statusOptions.find(s => s.id === selectedStatus)?.label || 'Todas'}
                        onClick={handleStatusFilterClick}
                        icon={<IconCircleFilled size={16} />}
                    />
                    <Filter
                        name="Comunidade"
                        valueLabel={communityOptions.find(c => c.id === selectedCommunity)?.label || 'Todas as comunidades'}
                        onClick={handleCommunityFilterClick}
                        icon={<IconUsers size={16} />}
                    />
                    <Filter
                        name="Data"
                        valueLabel={dateFilterOptions.find(d => d.id === selectedDateFilter)?.label || 'Sempre'}
                        onClick={handleDateFilterClick}
                        icon={<IconCalendar size={16} />}
                    />
                </div>

                <Popper controlRef={sharedPopperRef}>
                    <div style={{ display: 'none' }} />
                </Popper>
            </>
        )
    }

    // Adding Text import to the top or simulating it. 
    // Wait, I missed importing Text from @fragments. 
    // And FilterListRounded is used in FiltersRow but I didn't import it.

    function SubscriptionsTableSection() {
        const getStatusComponent = (status) => {
            const StatusComponent = {
                'active': StatusChip.Success,
                'canceled': StatusChip.Error,
                'expired': StatusChip.Alert,
                'default': StatusChip.Default,
                "unknown": StatusChip.Default
            }[status || 'default'];

            const statusLabels = {
                'active': 'Ativa',
                'canceled': 'Cancelada',
                'expired': 'Expirada',
                "unknown": "Não iniciada"
            };

            return <StatusComponent text={statusLabels[status] || 'Desconhecido'} />;
        };

        const handleUserClick = (e, userId) => {
            e.stopPropagation();
            if (e.ctrlKey || e.metaKey) {
                window.open(`/user/${userId}`, '_blank');
            } else {
                window.navigate(`/user/${userId}`);
            }
        };

        const handleCommunityClick = (e, communityId) => {
            e.stopPropagation();
            if (e.ctrlKey || e.metaKey) {
                window.open(`/${communityId}`, '_blank');
            } else {
                window.navigate(`/${communityId}`);
            }
        };

        const columns = [
            {
                id: 'subscriber',
                title: 'Assinante',
                customRender: (data) => (
                    <div className="subscriber-info pressable" onClick={(e) => handleUserClick(e, data.user?.id)}>
                        <UserAvatar user={data.user} size={22} />
                        <Text.span contrast={7} weight={500} style={{ marginLeft: '8px' }}>
                            {data.user?.username || 'N/A'}
                        </Text.span>
                    </div>
                )
            },
            {
                id: 'status',
                title: 'Status',
                align: 'center',
                customRender: (data) => getStatusComponent(data.status)
            },
            {
                id: 'plan',
                title: 'Plano',
                grow: 1,
                customRender: (data) => {
                    const plan = data.plan;
                    if (!plan) return <Text.span contrast={5}>Plano não encontrado</Text.span>;

                    const formatPrice = (price) => {
                        if (!price) return 'R$0,00';
                        return `R$${Parser.price.brl(price)}`;
                    };

                    const hasInstallments = data.method?.installments && data.method.installments > 1;
                    const installmentPrice = hasInstallments ? data.plan.price / data.method.installments : null;

                    return (
                        <>
                            <Text.span contrast={7} weight={500}>{plan.label || 'Plano sem nome'}: </Text.span>
                            <Text.span contrast={7}>
                                {hasInstallments
                                    ? `${data.method.installments}x ${formatPrice(installmentPrice)}`
                                    : formatPrice(plan.price)
                                }
                            </Text.span>
                            <Text.span contrast={4}> • {Parser.interval.noun(plan.interval) || plan.interval || 'N/A'}</Text.span>
                        </>
                    );
                }
            },
            {
                id: 'community',
                title: 'Comunidade',
                grow: 1,
                customRender: (data) => {
                    const community = communities?.find(c => c.id === data.community);
                    return (
                        <div className="community-info pressable" onClick={(e) => handleCommunityClick(e, community?.slug)}>
                            {community?.banner ? (
                                <img
                                    src={community.banner}
                                    alt={community.name}
                                    style={{
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '4px',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '4px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <IconUsers size={14} style={{ opacity: 0.5 }} />
                                </div>
                            )}
                            <Text.span contrast={7} weight={500} style={{ marginLeft: '8px' }}>
                                {community?.name || 'Comunidade não encontrada'}
                            </Text.span>
                        </div>
                    );
                }
            },
        ];

        const handleRowClick = (subscription) => {
            if (subscription.user?.id) {
                window.navigate(`/user/${subscription.user.id}`);
            }
        };

        return (
            <div className="subscriptions-table mt-3 mb-6">
                <Table
                    id="banking-subscriptions-table"
                    columns={columns}
                    data={subscriptions}
                    loading={loading}
                    loadingRows={4}
                    contained={true}
                    emptyMessage="Nenhuma assinatura encontrada"
                    enablePagination={true}
                    pages={pagination.pages}
                    records={pagination.records}
                    page={pagination.page}
                    onSelectPage={handlePageChange}
                    onRowClick={handleRowClick}
                />
            </div>
        )
    }

    return (
        <div>
            <SearchBar
                searchMode={searchMode}
                setSearchMode={setSearchMode}
                searchQueryRef={searchQueryRef}
                onSearch={executeSearch}
            />

            <FiltersRow />

            <div className="metrics-row mt-3">
                <MetricCard
                    title="Ativas"
                    metric={summary.active.records}
                    loading={loading}
                />
                <MetricCard
                    title="Canceladas"
                    metric={summary.canceled.records}
                    loading={loading}
                />
                <MetricCard
                    title="Expiradas"
                    metric={summary.expired.records}
                    loading={loading}
                />
            </div>

            <SubscriptionsTableSection />
        </div>
    )
}


